import { useState, useMemo } from 'react'
import { X, Calendar, Link } from 'lucide-react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { createClient } from '@/lib/supabase/client'
import { useAuth } from '@/lib/auth'
import { useNextEpisodeNumber, useCanAddEpisodeToSeries, useSeries } from '@/hooks/useSeries'
import { SeriesSelector } from '@/components/series/SeriesSelector'
import type { SeriesWithCount } from '@/types/series'
import { useLabels, singular } from '@/lib/terminology'

interface CreateEpisodeModalProps {
  isOpen: boolean
  onClose: () => void
  /** Pre-selected series ID (when creating from series detail page) */
  seriesId?: string
  /** Series name (for display when seriesId is provided) */
  seriesName?: string
  onSuccess?: () => void
}

function getDefaultDates(productionLeadDays: number = 7, recordingToReleaseDays: number = 7) {
  const today = new Date()
  const recordingDate = new Date(today)
  recordingDate.setDate(recordingDate.getDate() + productionLeadDays)
  const airDate = new Date(recordingDate)
  airDate.setDate(airDate.getDate() + recordingToReleaseDays)
  return {
    recording: recordingDate.toISOString().split('T')[0],
    air: airDate.toISOString().split('T')[0],
  }
}

function CreateEpisodeForm({
  initialSeriesId,
  onSuccess,
  onClose,
}: {
  initialSeriesId?: string
  onSuccess?: () => void
  onClose: () => void
}) {
  const { membership } = useAuth()
  const queryClient = useQueryClient()
  const supabase = createClient()
  const labels = useLabels()

  const [userSelectedSeriesId, setUserSelectedSeriesId] = useState<string | undefined>(initialSeriesId)
  const [title, setTitle] = useState('')
  const [description, setDescription] = useState('')
  const [meetingLink, setMeetingLink] = useState('')
  const [seriesError, setSeriesError] = useState<string>()

  // Fetch series list to auto-select the first one if none provided
  const { data: seriesList } = useSeries()

  // Derive the effective series ID: user selection > initial prop > first available series
  const selectedSeriesId = userSelectedSeriesId ?? (seriesList && seriesList.length > 0 ? seriesList[0].id : undefined)

  // Get the selected series object to access its timing settings
  const selectedSeries: SeriesWithCount | undefined = useMemo(() => {
    if (!selectedSeriesId || !seriesList) return undefined
    return seriesList.find(s => s.id === selectedSeriesId)
  }, [selectedSeriesId, seriesList])

  // Calculate default dates based on selected series
  const defaultDates = useMemo(() => {
    if (selectedSeries) {
      return getDefaultDates(
        selectedSeries.production_lead_days,
        selectedSeries.recording_to_release_days
      )
    }
    return getDefaultDates()
  }, [selectedSeries])

  // Track user overrides - null means use the default from series
  const [userRecordingDate, setUserRecordingDate] = useState<string | null>(null)
  const [userAirDate, setUserAirDate] = useState<string | null>(null)

  // Effective dates: user override or default from series
  const recordingDate = userRecordingDate ?? defaultDates.recording
  const airDate = userAirDate ?? defaultDates.air
  const hasUserEditedDates = userRecordingDate !== null || userAirDate !== null

  // Get next episode number for selected series
  const { data: nextNumber, isLoading: numberLoading } = useNextEpisodeNumber(selectedSeriesId)
  const { data: canAddEpisode, isLoading: canAddLoading } = useCanAddEpisodeToSeries(selectedSeriesId)

  const createEpisode = useMutation({
    mutationFn: async () => {
      if (!membership?.team_id || !selectedSeriesId || !title.trim()) {
        throw new Error('Missing required fields')
      }

      // Get the next episode number via RPC
      const { data: episodeNumber, error: numberError } = await supabase.rpc(
        'get_next_episode_number' as never,
        { p_series_id: selectedSeriesId } as never
      )

      if (numberError) throw numberError

      const { data, error } = await supabase
        .from('episodes')
        .insert({
          team_id: membership.team_id,
          series_id: selectedSeriesId,
          number: episodeNumber,
          title: title.trim(),
          description: description.trim() || null,
          recording_date: recordingDate,
          air_date: airDate,
          meeting_link: meetingLink.trim() || null,
          status: 'scheduled',
        } as never)
        .select()
        .single()

      if (error) throw error
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['episodes', selectedSeriesId] })
      queryClient.invalidateQueries({ queryKey: ['series'] })
      queryClient.invalidateQueries({ queryKey: ['canAddEpisode', selectedSeriesId] })
      queryClient.invalidateQueries({ queryKey: ['nextEpisodeNumber', selectedSeriesId] })
      queryClient.invalidateQueries({ queryKey: ['recordings'] })
      queryClient.invalidateQueries({ queryKey: ['schedule-episodes'] })
      onSuccess?.()
      onClose()
    },
  })

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!selectedSeriesId) {
      setSeriesError(`Please select a ${singular(labels.collections).toLowerCase()}`)
      return
    }

    if (!canAddEpisode) {
      setSeriesError(`Episode limit reached for this ${singular(labels.collections).toLowerCase()}`)
      return
    }

    try {
      await createEpisode.mutateAsync()
    } catch {
      // Error is handled by React Query's isError state
    }
  }

  const showSeriesSelector = !initialSeriesId

  return (
    <form onSubmit={handleSubmit} data-testid="create-episode-form">
      <div className="px-6 py-4 space-y-4">
        {/* Series selector (when not pre-selected) */}
        {showSeriesSelector && (
          <div data-testid="create-episode-series-section">
            <label className="block text-sm font-medium text-zinc-700 dark:text-zinc-300 mb-1">
              {singular(labels.collections)}
            </label>
            <SeriesSelector
              value={selectedSeriesId}
              onChange={(id) => {
                setUserSelectedSeriesId(id)
                setSeriesError(undefined)
                // Reset user date overrides to apply new series' timing defaults
                setUserRecordingDate(null)
                setUserAirDate(null)
              }}
              disabled={createEpisode.isPending}
              error={seriesError}
            />
            {seriesList && seriesList.length > 1 && (
              <p className="text-xs text-zinc-500 dark:text-zinc-400 mt-1">
                Change {singular(labels.collections).toLowerCase()} if needed, or use the default
              </p>
            )}
          </div>
        )}

        {/* Episode number (auto-assigned, readonly) */}
        {selectedSeriesId && (
          <div data-testid="create-episode-number-section">
            <label className="block text-sm font-medium text-zinc-700 dark:text-zinc-300 mb-1">
              Episode Number
            </label>
            <div className="px-3 py-2 rounded-lg border border-zinc-300 dark:border-zinc-600 bg-zinc-50 dark:bg-zinc-800 text-zinc-500 dark:text-zinc-400" data-testid="create-episode-number">
              {numberLoading ? 'Loading...' : `Episode ${nextNumber}`}
            </div>
            <p className="text-xs text-zinc-500 dark:text-zinc-400 mt-1">
              Automatically assigned based on existing episodes
            </p>
          </div>
        )}

        {/* Title */}
        <div>
          <label
            htmlFor="episode-title"
            className="block text-sm font-medium text-zinc-700 dark:text-zinc-300 mb-1"
          >
            Title <span className="text-rose-500">*</span>
          </label>
          <input
            id="episode-title"
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Episode title"
            disabled={createEpisode.isPending}
            className="w-full px-3 py-2 rounded-lg border border-zinc-300 dark:border-zinc-600 bg-white dark:bg-zinc-800 text-zinc-900 dark:text-zinc-100 placeholder-zinc-400 dark:placeholder-zinc-500 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent disabled:opacity-50"
            required
            data-testid="create-episode-title"
          />
        </div>

        {/* Description */}
        <div>
          <label
            htmlFor="episode-description"
            className="block text-sm font-medium text-zinc-700 dark:text-zinc-300 mb-1"
          >
            Description{' '}
            <span className="text-zinc-400 dark:text-zinc-500">(optional)</span>
          </label>
          <textarea
            id="episode-description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="What will this episode be about?"
            rows={3}
            disabled={createEpisode.isPending}
            className="w-full px-3 py-2 rounded-lg border border-zinc-300 dark:border-zinc-600 bg-white dark:bg-zinc-800 text-zinc-900 dark:text-zinc-100 placeholder-zinc-400 dark:placeholder-zinc-500 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent disabled:opacity-50 resize-none"
            data-testid="create-episode-description"
          />
        </div>

        {/* Dates */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label
              htmlFor="recording-date"
              className="block text-sm font-medium text-zinc-700 dark:text-zinc-300 mb-1"
            >
              <span className="flex items-center gap-1">
                <Calendar className="w-4 h-4" />
                Recording Date
              </span>
            </label>
            <input
              id="recording-date"
              type="date"
              value={recordingDate}
              onChange={(e) => setUserRecordingDate(e.target.value)}
              disabled={createEpisode.isPending}
              className="w-full px-3 py-2 rounded-lg border border-zinc-300 dark:border-zinc-600 bg-white dark:bg-zinc-800 text-zinc-900 dark:text-zinc-100 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent disabled:opacity-50"
              required
              data-testid="create-episode-recording-date"
            />
          </div>
          <div>
            <label
              htmlFor="air-date"
              className="block text-sm font-medium text-zinc-700 dark:text-zinc-300 mb-1"
            >
              <span className="flex items-center gap-1">
                <Calendar className="w-4 h-4" />
                Air Date
              </span>
            </label>
            <input
              id="air-date"
              type="date"
              value={airDate}
              onChange={(e) => setUserAirDate(e.target.value)}
              disabled={createEpisode.isPending}
              className="w-full px-3 py-2 rounded-lg border border-zinc-300 dark:border-zinc-600 bg-white dark:bg-zinc-800 text-zinc-900 dark:text-zinc-100 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent disabled:opacity-50"
              required
              data-testid="create-episode-air-date"
            />
          </div>
        </div>
        {selectedSeries && !hasUserEditedDates && (
          <p className="text-xs text-zinc-500 dark:text-zinc-400">
            Dates set based on {singular(labels.collections).toLowerCase()} timeline: {selectedSeries.production_lead_days} days lead time, {selectedSeries.recording_to_release_days} days to release
          </p>
        )}

        {/* Meeting Link */}
        <div>
          <label
            htmlFor="meeting-link"
            className="block text-sm font-medium text-zinc-700 dark:text-zinc-300 mb-1"
          >
            <span className="flex items-center gap-1">
              <Link className="w-4 h-4" />
              Meeting Link
            </span>
            <span className="text-zinc-400 dark:text-zinc-500 font-normal ml-1">
              (optional)
            </span>
          </label>
          <input
            id="meeting-link"
            type="url"
            value={meetingLink}
            onChange={(e) => setMeetingLink(e.target.value)}
            placeholder="https://zoom.us/j/... (or Calendly sets this automatically)"
            disabled={createEpisode.isPending}
            className="w-full px-3 py-2 rounded-lg border border-zinc-300 dark:border-zinc-600 bg-white dark:bg-zinc-800 text-zinc-900 dark:text-zinc-100 placeholder-zinc-400 dark:placeholder-zinc-500 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent disabled:opacity-50"
            data-testid="create-episode-meeting-link"
          />
          <p className="text-xs text-zinc-500 dark:text-zinc-400 mt-1">
            This will be included in guest invitation emails
          </p>
        </div>

        {/* Episode limit warning */}
        {selectedSeriesId && !canAddLoading && !canAddEpisode && (
          <div className="p-3 rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800" data-testid="create-episode-limit-warning">
            <p className="text-sm text-amber-800 dark:text-amber-200">
              You&apos;ve reached the episode limit for this {singular(labels.collections).toLowerCase()}.{' '}
              <a
                href="/pricing"
                className="font-medium underline hover:no-underline"
              >
                Upgrade your plan
              </a>{' '}
              to add more episodes.
            </p>
          </div>
        )}

        {createEpisode.isError && (
          <p className="text-sm text-rose-600 dark:text-rose-400" data-testid="create-episode-error">
            Failed to create episode. Please try again.
          </p>
        )}
      </div>

      {/* Footer */}
      <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-zinc-200 dark:border-zinc-800">
        <button
          type="button"
          onClick={onClose}
          disabled={createEpisode.isPending}
          className="px-4 py-2 text-sm font-medium text-zinc-700 dark:text-zinc-300 hover:bg-zinc-100 dark:hover:bg-zinc-800 rounded-lg transition-colors disabled:opacity-50"
          data-testid="create-episode-cancel"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={
            !selectedSeriesId ||
            !title.trim() ||
            !canAddEpisode ||
            createEpisode.isPending
          }
          className="px-4 py-2 text-sm font-medium text-white bg-primary-500 hover:bg-primary-600 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          data-testid="create-episode-submit"
        >
          {createEpisode.isPending ? 'Creating...' : 'Create Episode'}
        </button>
      </div>
    </form>
  )
}

export function CreateEpisodeModal({
  isOpen,
  onClose,
  seriesId: initialSeriesId,
  seriesName,
  onSuccess,
}: CreateEpisodeModalProps) {
  const labels = useLabels()
  // Use a key to reset the form when the modal is opened
  const [formKey, setFormKey] = useState(0)

  const handleClose = () => {
    setFormKey((k) => k + 1)
    onClose()
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center" data-testid="create-episode-modal">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/50" onClick={handleClose} data-testid="create-episode-modal-backdrop" />

      {/* Modal */}
      <div
        className="relative w-full max-w-md mx-4 bg-white dark:bg-zinc-900 rounded-xl shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-zinc-200 dark:border-zinc-800">
          <h2 className="text-lg font-semibold text-zinc-900 dark:text-zinc-100" data-testid="create-episode-modal-title">
            {initialSeriesId
              ? `Add Episode to ${seriesName || singular(labels.collections)}`
              : 'Create New Episode'}
          </h2>
          <button
            onClick={handleClose}
            className="p-1 rounded-lg hover:bg-zinc-100 dark:hover:bg-zinc-800 transition-colors"
            data-testid="create-episode-modal-close"
          >
            <X className="w-5 h-5 text-zinc-500" />
          </button>
        </div>

        {/* Form - key resets it when modal closes */}
        <CreateEpisodeForm
          key={formKey}
          initialSeriesId={initialSeriesId}
          onSuccess={onSuccess}
          onClose={handleClose}
        />
      </div>
    </div>
  )
}
